/**
 * Settings — shared by every role.
 *
 * Replaces `buildSettingsHTML` plus its four near-identical wrappers
 * (`renderDoctorSettings`, `renderPatientSettings`, `renderTeamSettings`,
 * `initSettings`) and the four password-change handlers that differed only in
 * which element ids they read.
 *
 * A note on what is genuinely available: the backend exposes
 * PUT /api/auth/profile, which updates only `picture` and `ringtone`. The legacy
 * UI also showed "change username", "change password", "link Gmail" and "verify
 * Gmail" forms — none of which had a server route; `handleChangePassword` wrote
 * to localStorage and reported success. Those forms are not reproduced, because
 * telling a user their password changed when it did not is worse than not
 * offering the option.
 *
 * المرحلة 1.1 turned the page into tabs (?tab= in the address, so a tab can
 * be linked to): «بياناتي» with the person's details and a verified email and
 * mobile, «التفضيلات» with the language, the notifications and the look,
 * «الأجهزة والجلسات», «كلمة السر والدخول», and «الموافقات». Every panel the
 * page had before is still here, in the tab it belongs to.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  ErrorState,
  Field,
  Icon,
  Input,
  Panel,
  SectionTitle,
  Spinner,
  Tabs,
} from '../../components/common';
import { HeroBand } from '../../components/common/Tile3D';
import { useI18n } from '../../i18n/I18nProvider';
import { ConsentsTab, PreferencesTab, ProfileTab, SessionsTab } from './AccountTabs';
import { useAuth, ROLES } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { authApi, doctorApi } from '../../api/endpoints';
import { normalizeDoctor } from '../../domain/catalog';
import { compressImage, readFileAsDataUri, formatBytes, dataUriByteLength } from '../../domain/files';
import { alarmService } from '../../services/alarmService';
import { useData } from '../../context/DataContext';
import { PrescriptionDesigner } from './PrescriptionDesigner';
import { GoogleAccounts } from './GoogleAccounts';
import { ChangePasswordForm } from './ChangePassword';
import { ThemePicker } from './ThemePicker';
import { usePermissions } from '../../context/PermissionContext';
import { ROLE_TITLES } from '../../domain/rbac';

const TAB_IDS = ['profile', 'prefs', 'sessions', 'security', 'consents', 'clinic'];

export function SettingsPage() {
  const { session, role, doctorProfile, setDoctorProfile, updateSession, logout } = useAuth();
  const { success, error: toastError } = useNotifications();
  const { roleTitle, refresh: refreshPermissions, serviceShown } = usePermissions();
  const { t, tr } = useI18n();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab = TAB_IDS.includes(requested) && (requested !== 'clinic' || role === ROLES.DOCTOR) ? requested : 'profile';
  const setTab = (id) => setParams(id === 'profile' ? {} : { tab: id }, { replace: true });

  // «بياناتي»: the account as the server holds it, with the scope's rules.
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState(null);
  const loadMe = useCallback(async () => {
    setMeError(null);
    try {
      setMe(await authApi.me());
    } catch (err) {
      setMeError(err);
    }
  }, []);
  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const [avatar, setAvatar] = useState(null);
  const [ringtone, setRingtone] = useState(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [savingRingtone, setSavingRingtone] = useState(false);

  useEffect(() => {
    setAvatar(doctorProfile?.image || session?.picture || null);
  }, [doctorProfile?.image, session?.picture]);

  // The tone saved on the account, so «تجربة النغمة» plays it and it can be reset.
  const hasDoseAlarm = role === ROLES.PATIENT || role === ROLES.TEAM_MEMBER;
  useEffect(() => {
    if (!hasDoseAlarm) return undefined;
    let live = true;
    authApi
      .ringtone()
      .then((result) => {
        if (!live) return;
        setRingtone(result?.ringtone || null);
        alarmService.setCustomRingtone(result?.ringtone || null);
      })
      .catch(() => {
        /* the default tone stays */
      });
    return () => {
      live = false;
    };
  }, [hasDoseAlarm]);

  const handleAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSavingAvatar(true);
    try {
      // Avatars are stored as base64 on the user row, so they must be small.
      const dataUri = await compressImage(file, { maxWidth: 400, maxHeight: 400, quality: 0.8 });
      await authApi.updateProfile({ picture: dataUri });

      setAvatar(dataUri);
      updateSession({ picture: dataUri });

      // A doctor's avatar is mirrored onto their DoctorProfile by the backend,
      // so the local copy has to be refreshed to match.
      if (role === ROLES.DOCTOR) {
        const fresh = await doctorApi.profile();
        setDoctorProfile(normalizeDoctor(fresh));
      }

      success(tr('تم تحديث الصورة الشخصية'));
    } catch (err) {
      toastError(err.message || tr('تعذر تحديث الصورة'));
    } finally {
      setSavingAvatar(false);
    }
  };

  const handleRingtone = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSavingRingtone(true);
    try {
      const dataUri = await readFileAsDataUri(file);
      const size = dataUriByteLength(dataUri);

      // The API body limit is 50 MB; a ringtone anywhere near that is a mistake.
      if (size > 3 * 1024 * 1024) {
        throw new Error(`الملف كبير جداً (${formatBytes(size)}). اختر ملفاً أقل من 3 ميجابايت.`);
      }

      await authApi.updateProfile({ ringtone: dataUri });
      setRingtone(dataUri);
      alarmService.setCustomRingtone(dataUri);
      success(tr('تم حفظ نغمة التنبيه'));
    } catch (err) {
      toastError(err.message || tr('تعذر حفظ النغمة'));
    } finally {
      setSavingRingtone(false);
    }
  };

  const clearRingtone = async () => {
    setSavingRingtone(true);
    try {
      await authApi.updateProfile({ ringtone: null });
      setRingtone(null);
      alarmService.setCustomRingtone(null);
      success(tr('تمت العودة للنغمة الافتراضية'));
    } catch (err) {
      toastError(err.message || tr('تعذر إزالة النغمة'));
    } finally {
      setSavingRingtone(false);
    }
  };

  const tabs = [
    { id: 'profile', label: t('settings.tabProfile'), icon: 'user-round' },
    { id: 'prefs', label: t('settings.tabPrefs'), icon: 'sliders-horizontal' },
    { id: 'sessions', label: t('settings.tabSessions'), icon: 'monitor-smartphone' },
    { id: 'security', label: t('settings.tabSecurity'), icon: 'key-round' },
    { id: 'consents', label: t('settings.tabConsents'), icon: 'scale' },
    ...(role === ROLES.DOCTOR ? [{ id: 'clinic', label: t('settings.tabClinic'), icon: 'stethoscope' }] : []),
  ];

  const needsMe = ['profile', 'prefs', 'sessions'].includes(tab);

  return (
    <div className="space-y-6">
      <HeroBand icon="shield-check" title={t('settings.title')} subtitle={t('settings.subtitle')} tone="cyan">
        <div className="settings-who">
          <div className="relative">
            <Avatar src={avatar} name={session?.name} size={52} className="settings-who__avatar" />
            {savingAvatar && (
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/60">
                <Icon name="loader-2" size={18} className="animate-spin text-cyan-400" />
              </span>
            )}
            <label className="settings-who__cam" title={tr('تغيير الصورة')}>
              <Icon name="camera" size={13} />
              <input type="file" accept="image/*" onChange={handleAvatar} className="hidden" />
            </label>
          </div>
          <div className="min-w-0">
            <p className="truncate font-bold text-white">{session?.name}</p>
            <p className="truncate text-xs text-slate-400" dir="ltr" style={{ textAlign: 'start' }}>
              @{session?.username}
            </p>
            <Badge className="mt-1 border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="shield">
              {tr(roleTitle || ROLE_TITLES[role] || role)}
            </Badge>
          </div>
        </div>
      </HeroBand>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {needsMe && meError && <ErrorState error={meError} onRetry={loadMe} />}
      {needsMe && !me && !meError && <Spinner variant="inline" label={t('common.loading')} />}

      {tab === 'profile' && me && <ProfileTab me={me} onChanged={loadMe} />}

      {tab === 'prefs' && me && (
        <>
          <PreferencesTab me={me} onChanged={loadMe} />
          {/* Appearance — every role picks its own dashboard colours */}
          {/* «8 ثيمات» is a key: closed, everyone stays on the platform's look. */}
          {serviceShown('themes') && <ThemePicker />}
          {/* Alarm ringtone — patients are the ones with dose alarms */}
          {hasDoseAlarm && (
            <Panel>
              <SectionTitle icon="bell-ring">{tr('نغمة تنبيه الدواء')}</SectionTitle>
              <p className="mb-4 text-sm leading-relaxed text-slate-400">{tr('اختر نغمة مخصصة تُشغَّل عند موعد الجرعة، أو استخدم النغمة الافتراضية.')}</p>
              <div className="flex flex-wrap gap-2">
                <label className="btn-ghost cursor-pointer">
                  <Icon name="upload" size={16} />
                  {tr('رفع نغمة')}
                  <input type="file" accept="audio/*" onChange={handleRingtone} className="hidden" />
                </label>
                <Button variant="subtle" icon="play" onClick={() => alarmService.preview()}>
                  {tr('تجربة النغمة')}
                </Button>
                {ringtone && (
                  <Button variant="subtle" icon="rotate-ccw" loading={savingRingtone} onClick={clearRingtone}>
                    {tr('العودة للافتراضية')}
                  </Button>
                )}
              </div>
            </Panel>
          )}
        </>
      )}

      {tab === 'sessions' && me && <SessionsTab me={me} />}

      {tab === 'security' && (
        <>
          {/* Password — the route this form always needed now exists */}
          <Panel>
            <SectionTitle icon="key-round">{tr('كلمة المرور')}</SectionTitle>
            <ChangePasswordForm onChanged={refreshPermissions} />
          </Panel>
          {/* Google sign-in addresses — every role, since every role signs in */}
          {serviceShown('googleLogin') && <GoogleAccounts />}
          {/* This device's session */}
          <Panel>
            <SectionTitle icon="log-out">{tr('الجلسة')}</SectionTitle>
            <p className="mb-4 text-sm leading-relaxed text-slate-400">{tr('تسجيل الخروج ينهي جلستك على هذا الجهاز فقط.')}</p>
            <Button variant="danger" icon="log-out" onClick={logout}>
              {t('shell.logout')}
            </Button>
          </Panel>
        </>
      )}

      {tab === 'consents' && <ConsentsTab />}

      {/* Doctor-specific */}
      {tab === 'clinic' && role === ROLES.DOCTOR && <DoctorProfileSection />}
    </div>
  );
}

/* ========================================================================== */

/** Prices, title and print branding — the fields PUT /api/doctors/profile accepts. */
function DoctorProfileSection() {
  const { serviceShown } = usePermissions();
  const { doctorProfile, setDoctorProfile } = useAuth();
  const { categories } = useData();
  const { success, error: toastError } = useNotifications();

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoInput = useRef(null);

  useEffect(() => {
    if (!doctorProfile) return;
    setForm({
      title: doctorProfile.title || '',
      categoryId: doctorProfile.category || '',
      priceExam: doctorProfile.price_exam ?? 500,
      priceFollowup: doctorProfile.price_followup ?? 200,
      logo: doctorProfile.logo || '',
      prescriptionStyle: doctorProfile.prescriptionStyle || 'wave-top',
      printPrimaryColor: doctorProfile.printColors?.primary || '#06b6d4',
      printSecondaryColor: doctorProfile.printColors?.secondary || '#a855f7',
    });
  }, [doctorProfile]);

  if (!form) return null;

  const update = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const handleLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      // PNG, not the JPEG default: a logo's transparent background would
      // otherwise turn black on the printed letterhead.
      const dataUri = await compressImage(file, { maxWidth: 500, maxHeight: 500, quality: 0.85, mimeType: 'image/png' });
      setForm((current) => ({ ...current, logo: dataUri }));
    } catch (err) {
      toastError(`تعذر قراءة الشعار: ${err.message}`);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await doctorApi.updateProfile({
        ...form,
        priceExam: Number(form.priceExam),
        priceFollowup: Number(form.priceFollowup),
        // Standing discounts are owned by the offers screen; re-send them
        // unchanged so this update does not clear them.
        hasNightDiscount: doctorProfile.hasNightDiscount,
        nightDiscountPercent: doctorProfile.nightDiscountPercent,
        hasFirstVisitDiscount: doctorProfile.hasFirstVisitDiscount,
        firstVisitDiscountPercent: doctorProfile.firstVisitDiscountPercent,
      });

      const fresh = await doctorApi.profile();
      setDoctorProfile(normalizeDoctor(fresh));
      success('تم حفظ بيانات العيادة');
    } catch (err) {
      toastError(err.message || 'تعذر حفظ البيانات');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel>
      <SectionTitle icon="stethoscope">بيانات الطبيب</SectionTitle>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="اللقب المهني" hint="مثال: استشاري جراحة عامة">
            <Input value={form.title} onChange={update('title')} />
          </Field>

          <Field label="التخصص">
            <select value={form.categoryId} onChange={update('categoryId')} className="field-input">
              <option value="">اختر التخصص</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="سعر الكشف الافتراضي" hint="يُستخدم عندما لا تحدد العيادة سعراً خاصاً">
            <Input type="number" min="0" value={form.priceExam} onChange={update('priceExam')} />
          </Field>

          <Field label="سعر المتابعة الافتراضي">
            <Input
              type="number"
              min="0"
              value={form.priceFollowup}
              onChange={update('priceFollowup')}
            />
          </Field>
        </div>

        <div className="border-t border-white/10 pt-4">
          <h4 className="mb-3 text-sm font-bold text-cyan-300">هوية الروشتة المطبوعة</h4>

          <div className="flex flex-wrap items-center gap-4">
            {form.logo && (
              <img
                src={form.logo}
                alt="شعار العيادة"
                className="h-16 w-16 rounded-xl border border-white/10 bg-white object-contain p-1"
              />
            )}

            <Button variant="ghost" icon="image" onClick={() => logoInput.current?.click()}>
              {form.logo ? 'تغيير الشعار' : 'رفع شعار'}
            </Button>
            <input
              ref={logoInput}
              type="file"
              accept="image/*"
              onChange={handleLogo}
              className="hidden"
            />

            {form.logo && (
              <Button
                variant="subtle"
                icon="trash-2"
                onClick={() => setForm((c) => ({ ...c, logo: '' }))}
                className="!text-red-300"
              >
                إزالة
              </Button>
            )}
          </div>

          <div className="mt-5 border-t border-white/10 pt-5">
            {/* «مصمم الروشتة» is a key of its own in an institution's package. */}
            {serviceShown('rxDesigner') && <PrescriptionDesigner form={form} setForm={setForm} doctorProfile={doctorProfile} />}
          </div>
        </div>

        <div className="flex justify-end border-t border-white/10 pt-4">
          <Button icon="save" loading={saving} onClick={save}>
            حفظ البيانات
          </Button>
        </div>
      </div>
    </Panel>
  );
}

export default SettingsPage;
